<template>
  <div id="data-list-list-view" class="datatable-container">
    <!-- Page Header -->
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-2xl font-semibold">{{ $t('nav.receivedOrders') }}</h2>
      <vs-button
        color="primary"
        type="border"
        icon-pack="feather"
        icon="icon-refresh-cw"
        :disabled="loading"
        @click="getRecords"
      >
        {{ $t('dashboard.refresh') }}
      </vs-button>
    </div>

    <p class="text-sm text-gray-600 mb-4">
      Inbound Peppol procurement documents received as the seller — Orders and their
      Changes, Cancellations and Balance updates (Peppol BIS Ordering).
    </p>

    <!-- Filters -->
    <vx-card>
      <div class="vx-row">
        <div class="vx-col md:w-1/4 w-full mb-3">
          <label class="text-sm">Document Type</label>
          <vs-select v-model="filters.documentType" class="w-full" @input="onFilterChange">
            <vs-select-item :value="''" text="All Types" />
            <vs-select-item value="Order" text="Order" />
            <vs-select-item value="OrderChange" text="Order Change" />
            <vs-select-item value="OrderCancellation" text="Order Cancellation" />
            <vs-select-item value="OrderBalance" text="Order Balance" />
          </vs-select>
        </div>
        <div class="vx-col md:w-1/4 w-full mb-3">
          <label class="text-sm">Status</label>
          <vs-select v-model="filters.status" class="w-full" @input="onFilterChange">
            <vs-select-item :value="''" text="All Statuses" />
            <vs-select-item value="RECEIVED" text="Received" />
            <vs-select-item value="CHANGED" text="Changed" />
            <vs-select-item value="CANCELLED" text="Cancelled" />
            <vs-select-item value="RESPONDED" text="Responded" />
            <vs-select-item value="CONVERTED" text="Converted" />
          </vs-select>
        </div>
        <div class="vx-col md:w-1/2 w-full mb-3">
          <label class="text-sm">Search (order # / buyer / sender PID)</label>
          <vs-input
            class="w-full"
            icon-pack="feather"
            icon="icon-search"
            v-model="filters.search"
            @keyup.enter="onFilterChange"
          />
        </div>
      </div>
    </vx-card>

    <!-- Results Table -->
    <vx-card class="mt-5">
      <h4 class="card-title">{{ $t('dashboard.results') }}</h4>

      <vs-table
        v-show="loaded"
        stripe
        ref="table"
        :data="orders"
        :noDataText="$t('dashboard.noDataAvailable')"
      >
        <template slot="thead">
          <!-- Sortable headers pending: /peppol-inbound-orders has no sort param
               in its search DTO yet (api-core PeppolInboundOrderSearchDto). -->
          <vs-th>Order #</vs-th>
          <vs-th>Type</vs-th>
          <vs-th>Buyer</vs-th>
          <vs-th>Issue Date</vs-th>
          <vs-th>Amount</vs-th>
          <vs-th>Outstanding</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>{{ $t('dashboard.action') }}</vs-th>
        </template>

        <template slot-scope="{ data }">
          <tbody>
            <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
              <vs-td>
                <span class="font-medium cursor-pointer text-primary" @click="handleView(tr)">
                  {{ tr.orderId || '—' }}
                </span>
                <p v-if="tr.orderReferenceId" class="text-xs text-gray-500">
                  ref: {{ tr.orderReferenceId }}
                </p>
              </vs-td>
              <vs-td>
                <vs-chip :color="typeColor(tr.documentType)">{{ typeLabel(tr.documentType) }}</vs-chip>
              </vs-td>
              <vs-td>
                <p class="text-sm">{{ tr.buyerName || '—' }}</p>
                <p class="text-xs text-gray-500">{{ tr.senderPid }}</p>
              </vs-td>
              <vs-td>
                <span v-if="tr.issueDate" class="text-sm">{{ tr.issueDate | moment('DD MMM YYYY') }}</span>
                <span v-else class="text-gray-400">—</span>
              </vs-td>
              <vs-td>
                <span class="text-sm">{{ formatAmount(tr.payableAmount, tr.documentCurrency) }}</span>
              </vs-td>
              <vs-td>
                <span
                  v-if="tr.outstandingBalance != null"
                  class="text-sm font-medium"
                  :class="tr.outstandingBalance > 0 ? 'text-warning' : 'text-success'"
                >
                  {{ formatAmount(tr.outstandingBalance, tr.documentCurrency) }}
                </span>
                <span v-else class="text-gray-400">—</span>
              </vs-td>
              <vs-td>
                <vs-chip :color="statusColor(tr.status)">{{ tr.status }}</vs-chip>
                <feather-icon
                  v-if="tr.changeCount > 0"
                  icon="Edit2Icon"
                  svgClasses="w-3 h-3 ml-1 inline text-warning"
                  :title="tr.changeCount + ' change(s) applied'"
                />
              </vs-td>
              <vs-td>
                <feather-icon
                  icon="EyeIcon"
                  svgClasses="w-5 h-5 hover:text-primary stroke-current"
                  class="cursor-pointer"
                  @click.stop="handleView(tr)"
                />
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>

      <div v-if="loading" class="flex justify-center items-center py-10">
        <vs-spinner color="primary" />
      </div>

      <div v-if="loaded && orders.length === 0" class="text-center py-10">
        <feather-icon icon="InboxIcon" svgClasses="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p class="text-gray-500">No inbound orders found</p>
      </div>

      <template v-if="totalPage > 1">
        <div class="flex justify-end mt-4">
          <vs-pagination :total="totalPage" v-model="currentPage"></vs-pagination>
        </div>
      </template>
    </vx-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      itemsPerPage: 25,
      loaded: false,
      loading: false,
      currentPage: 1,
      totalItem: 0,
      totalPage: 0,
      filters: {
        documentType: '',
        status: '',
        search: '',
      },
    }
  },
  computed: {
    orders() {
      return this.$store.getters['peppolInboundOrders/orders']
    },
  },
  watch: {
    currentPage(newValue, oldValue) {
      if (newValue !== oldValue) this.getRecords()
    },
  },
  created() {
    this.getRecords()
  },
  methods: {
    async getRecords() {
      this.loading = true
      try {
        const params = { page: this.currentPage, limit: this.itemsPerPage }
        if (this.filters.documentType) params.documentType = this.filters.documentType
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.search) params.search = this.filters.search

        await this.$store.dispatch('peppolInboundOrders/fetchRecords', params)
        const pagination = this.$store.state.peppolInboundOrders.pagination || {}
        this.currentPage = pagination.currentPage || 1
        this.totalItem = pagination.totalItem || this.orders.length
        this.totalPage = pagination.totalPage || 1
        this.loaded = true
      } catch (e) {
        console.error('[InboundOrders] fetch failed:', e)
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: e.response?.data?.message || 'Failed to fetch inbound orders',
        })
      } finally {
        this.loading = false
      }
    },

    onFilterChange() {
      this.currentPage = 1
      this.getRecords()
    },

    handleView(item) {
      this.$router.push('/received-orders/' + item.id).catch(() => {})
    },

    typeLabel(type) {
      return (
        {
          Order: 'Order',
          OrderChange: 'Change',
          OrderCancellation: 'Cancellation',
          OrderBalance: 'Balance',
        }[type] || type
      )
    },
    typeColor(type) {
      return (
        {
          Order: 'primary',
          OrderChange: 'warning',
          OrderCancellation: 'danger',
          OrderBalance: 'dark',
        }[type] || 'primary'
      )
    },
    statusColor(status) {
      return (
        {
          RECEIVED: 'primary',
          CHANGED: 'warning',
          CANCELLED: 'danger',
          RESPONDED: 'success',
          CONVERTED: 'success',
        }[status] || 'primary'
      )
    },
    formatAmount(amount, currency) {
      if (amount == null || isNaN(amount)) return '—'
      const value = Number(amount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return currency ? `${currency} ${value}` : value
    },
  },
}
</script>

<style lang="scss" scoped>
.datatable-container {
  .vs-table--tbody-table {
    .vs-table--td {
      padding: 12px 8px;
    }
  }
}
</style>
